<template>
  <m-validation-component class="m-datetime" :errors="displayErrors">
    <m-input-group
      horizontal
      :force-horizontal-label="horizontal"
      :label="label"
      :required="$attrs.required"
      :helper="helper"
      :instructions="instructions"
      :data-testid="$attrs['data-testid'] || name || undefined"
      :label-width="labelWidth"
      left
    >
      <template v-if="$slots.helper" #helper> <slot name="helper" /></template>

      <m-input
        v-model="dateShowText"
        class="date-input"
        :for="dateFor"
        label="Date"
        :input-format="dateMask"
        :placeholder="datePlaceholder"
        :horizontal="false"
        :disable="$attrs.disabled || $attrs.disable"
        :disabled="$attrs.disabled || $attrs.disable"
        :large="large"
        :name="name ? `${name}:date` : undefined"
        :invalid="!!errors?.length"
        @blur="onBlur"
        @focus="onFocus"
        @keydown.capture="onKeydown"
      >
        <template #append>
          <m-button
            ref="calendarButton"
            :aria-label="`Open date picker for ${dateShowText}`"
            icon="fa-light fa-calendar"
            icon-only
            ghost
            small
            color="secondary"
            aria-haspopup="dialog"
            :aria-expanded="showForm"
            @click="showForm = !showForm"
          >
            <span class="sr-only">Open date picker for {{ dateShowText }}</span>
          </m-button>
        </template>
      </m-input>

      <m-input
        :model-value="timeText"
        :for="timeFor"
        class="time-input"
        :placeholder="timePlaceholder"
        :horizontal="false"
        label="Time"
        mask="##:##"
        :name="name ? `${name}:time` : undefined"
        :disable="$attrs.disabled || $attrs.disable"
        :disabled="$attrs.disabled || $attrs.disable"
        :invalid="!!errors?.length"
        @blur="onComponentBlur"
      />
    </m-input-group>
  </m-validation-component>

  <Teleport to="body">
    <div v-if="showForm" ref="popupEl" :style="popupStyle">
      <m-calendar-grid
        :model-value="dateText"
        :min-date="resolvedMin"
        :max-date="resolvedMax"
        @update:model-value="onCalendarSelect"
        @close="showForm = false"
      >
        <template #header-actions>
          <m-button
            icon="fa-solid fa-xmark"
            icon-only
            color="secondary"
            sr-label="Close"
            :outline="false"
            :small="true"
            :dense="true"
            class="borderless"
            @click="showForm = false"
          />
        </template>
      </m-calendar-grid>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import MButton from "../../MButton";
import MInputGroup from "./../../MInputGroup";
import MInput from "./../MInput";
import MValidationComponent from "./../MValidationComponent";
import MCalendarGrid from "../MCalendarGrid/MCalendarGrid.vue";

import { computed, nextTick, provide, ref, watch } from "vue";

defineOptions({ inheritAttrs: false });
import { useDateValue } from "../../../../composables/dateValue";
import { usePopupPosition } from "../../../../composables/usePopupPosition";
import { getMaxDate, getMinDate } from "../../../../utils/helpers";
import { addToDate, formatDateTimeMedicus } from "../../../../utils/date";
// import MDate css
import "../MDate";

const props = defineProps({
  label: String,
  description: String,
  modelValue: {
    type: [Object, String, Number, Array],
    required: false,
  },
  name: String,
  rules: Array as () => Array<(v: any) => boolean | string>,
  showAllErrors: Boolean,
  // m-labelled text props
  boldText: {
    type: Boolean,
    default: false,
  },
  horizontal: {
    type: Boolean,
    default: undefined,
  },
  labelWidth: String,
  timePlaceholder: {
    type: String,
    default() {
      return "hh:mm";
    },
  },
  datePlaceholder: {
    type: String,
    default() {
      return "dd/mm/yyyy";
    },
  },
  minDate: {
    type: [Date, String],
    default() {
      return new Date(1000, 1, 1);
    },
  },
  maxDate: {
    type: [Date, String],
    default() {
      return new Date(new Date().getFullYear() + 100, 1, 1);
    },
  },
  minDateText: {
    type: String,
  },
  maxDateText: {
    type: String,
  },
  requiredText: String,
  seconds: {
    type: String,
    default: "00",
  },
  large: Boolean,
  helper: String,
  instructions: String,
  masked: Boolean,

  for: String,
});

const dateFor = computed(() => (props.for ? `${props.for}.date` : undefined));
const timeFor = computed(() => (props.for ? `${props.for}.time` : undefined));

const showForm = ref(false);
const calendarButton = ref(null);

watch(showForm, (open) => {
  if (!open) nextTick(() => (calendarButton.value as any)?.focus());
});

const { popupEl, popupStyle } = usePopupPosition({
  isOpen: showForm,
  getTrigger: () => (calendarButton.value as any)?.$el as HTMLElement | null,
});

provide("input-dont-use-name", true);

const { dateText, timeText, errors, displayErrors, displayDate, touched } = useDateValue(
  props,
  true,
  undefined,
  undefined,
  () => props.requiredText || `Enter a date and time for '${props.label}'`,
);

const resolvedMin = computed(() => getMinDate(props.minDate, true));
const resolvedMax = computed(() => getMaxDate(props.maxDate, true));

function onCalendarSelect(value: string) {
  dateText.value = value;
  showForm.value = false;
}

const tokenRegex = /-?(\d+)([d|w|m|y])/g;

function parseDate(d: string, splitter: string) {
  let [day, month, year, ...rest] = d.split(splitter);

  if (!day || !month || !year || rest?.length) {
    return undefined;
  }

  if (year && +year < 100) {
    year = "20" + year.padStart(2, "0");
  }

  return [day, month, year].join("/");
}

function parseToken(t: string) {
  const matches = t.match(tokenRegex);
  if (!matches) {
    return;
  }

  let d = new Date();

  for (const t of matches) {
    const v = +t.slice(0, -1);

    switch (t[t.length - 1]) {
      case "d": {
        d = addToDate(d, { days: v });
        break;
      }
      case "w": {
        d = addToDate(d, { days: v * 7 });
        break;
      }
      case "m": {
        d = addToDate(d, { months: v });
        break;
      }
      case "y": {
        d = addToDate(d, { years: v });
        break;
      }
    }
  }
  return formatDateTimeMedicus(d);
}

const shouldMask = ref(false);
const dateMask = computed(() => (shouldMask.value ? "dd/mm/yyyy" : undefined));

function onKeydown(e: KeyboardEvent) {
  if (props.masked) return;
  switch (e.key?.toLowerCase()) {
    case "-":
    case "d":
    case "w":
    case "m":
    case "y":
      shouldMask.value = false;
      e.stopImmediatePropagation();
      break;
    case "/":
      shouldMask.value = true;
      break;
    default: {
      if (e.code.startsWith("Key") && !e.altKey && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
      }
    }
  }
}

function onFocus() {
  dateShowText.value = dateText.value;
  nextTick(() => {
    shouldMask.value = !!dateText.value;
  });
}

const dateShowText = ref(displayDate.value);

async function onBlur() {
  try {
    shouldMask.value = false;
    if (props.masked) {
      return;
    }

    let text = dateShowText.value ?? "";

    if (!Number.isNaN(text) && text.length === 8) {
      // if the text is a number and has 8 digits
      text = `${text.slice(0, 2)}/${text.slice(2, 4)}/${text.slice(4)}`;
    }

    let actualDate = parseDate(text, "/");
    if (!actualDate) {
      actualDate = parseDate(text, ".");
      if (!actualDate) {
        actualDate = parseToken(text.toLowerCase());
        if (!actualDate) {
          // invalid date
          return;
        }
      }
    }

    dateText.value = actualDate.split(" ")[0].split("-").reverse().join("/");
  } finally {
    await nextTick();
    dateShowText.value = displayDate.value;
  }
}

watch(displayDate, (t, p) => {
  if (dateShowText.value === p) {
    dateShowText.value = t;
  }
});

watch(dateShowText, (t) => {
  // slash cannot be the first character
  shouldMask.value = t?.indexOf("/") > 0;
});

function onComponentBlur(v: FocusEvent) {
  touched.value = true;
  if (!v?.target?.value) return;
  const padded: string = (v?.target?.value ?? "").padEnd(5, "0");

  const time = `${padded.substring(0, 2)}:${padded.substring(3, 5)}`;
  timeText.value = time;
}
</script>
<style lang="scss">
.m-datetime {
  &.large {
    .date-input {
      flex: 1 1 auto;
    }
  }

  // margin-bottom: 12px;
  .labeled-text > .text {
    padding: 0;
  }

  .labeled-text {
    // margin-bottom: 0;
    padding-bottom: 0;
  }

  .m-input {
    margin: 0;
  }

  > .validation-component > .m-input-group.horizontal {
    .date-wrapper {
      .date-input {
        margin-right: 0;
      }
    }
    > .label {
      margin-top: 10px !important;
    }
  }

  .date-wrapper {
    display: flex;
    &.datetime {
      .date-input {
        // margin-right: 1em;
      }
    }

    &.time:not(.date) {
      > div {
        flex: 1 1 auto;
      }
    }
  }

  .time-input {
    width: 5rem;
  }
}
</style>
